import { motion } from "framer-motion";
import { Sparkles, ArrowRight, CheckCircle2, Wand2, TrendingUp } from "lucide-react";

export default function AIOptimizationSection() {
  return (
    <section className="py-24 bg-white" data-testid="ai-optimization-section">
      <div className="max-w-[1200px] mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold text-white mb-4" style={{ background: 'linear-gradient(135deg, #F97316 0%, #D946EF 100%)' }}>
            <Sparkles size={12} /> NEW
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#0F172A' }}>
            <span className="gradient-text">AI Image Optimization</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed max-w-2xl mx-auto" style={{ color: '#475569' }}>
            Don't just see what to fix — let AI fix it for you. Select the recommendations you want to apply,
            click optimize, and get a redesigned version of your ad in seconds.
          </p>
        </motion.div>

        {/* Before / After with central improvement card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto mb-16"
        >
          <div className="grid md:grid-cols-2 gap-6 md:gap-12">
            {/* BEFORE */}
            <div className="relative">
              <div className="absolute -top-3 left-4 z-10 bg-slate-200 text-slate-700 text-[11px] font-bold px-3 py-1 rounded-full">
                BEFORE
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
                <img
                  src="/before-ad.png"
                  alt="Original ad before AI optimization"
                  className="w-full aspect-square object-cover"
                />
                <div className="p-4 bg-slate-50 border-t border-slate-100">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">Neuro Score</span>
                    <span className="font-bold text-amber-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>54</span>
                  </div>
                </div>
              </div>
            </div>

            {/* AFTER */}
            <div className="relative">
              <div className="absolute -top-3 left-4 z-10 text-white text-[11px] font-bold px-3 py-1 rounded-full flex items-center gap-1" style={{ background: 'linear-gradient(135deg, #F97316 0%, #D946EF 100%)' }}>
                <Sparkles size={10} /> AFTER
              </div>
              <div className="bg-white rounded-2xl border-2 overflow-hidden shadow-lg" style={{ borderColor: '#F97316' }}>
                <img
                  src="/after-ad.png"
                  alt="Optimized ad after AI optimization"
                  className="w-full aspect-square object-cover"
                />
                <div className="p-4 bg-orange-50 border-t border-orange-100">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">Neuro Score</span>
                    <span className="font-bold text-emerald-600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      82 <span className="text-[10px] text-emerald-500">+28</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Central improvement card — desktop only, overlapping */}
          <div className="hidden md:flex absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none">
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 p-5 w-[200px]" style={{ boxShadow: '0 20px 40px rgba(249, 115, 22, 0.15), 0 0 0 1px rgba(249, 115, 22, 0.1)' }}>
              <div className="flex items-center justify-center mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F97316 0%, #D946EF 100%)' }}>
                  <TrendingUp size={18} className="text-white" />
                </div>
              </div>
              <p className="text-[10px] font-bold text-center text-slate-400 uppercase tracking-wide mb-3">Predicted Impact</p>
              <div className="space-y-2.5">
                <KPI label="Neuro Score" value="+28" />
                <KPI label="Engagement" value="+47%" />
                <KPI label="CTR" value="+62%" />
              </div>
            </div>
          </div>

          {/* Mobile improvement card — between cards */}
          <div className="md:hidden flex justify-center my-6">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-4 w-full max-w-[280px]">
              <div className="flex items-center justify-center mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F97316 0%, #D946EF 100%)' }}>
                  <TrendingUp size={18} className="text-white" />
                </div>
              </div>
              <p className="text-[10px] font-bold text-center text-slate-400 uppercase tracking-wide mb-3">Predicted Impact</p>
              <div className="grid grid-cols-3 gap-2">
                <KPI label="Score" value="+28" small />
                <KPI label="Engagement" value="+47%" small />
                <KPI label="CTR" value="+62%" small />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Feature highlights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto"
        >
          <FeatureBox
            icon={CheckCircle2}
            title="Pick what to apply"
            desc="Check only the recommendations you want — full control over what changes."
          />
          <FeatureBox
            icon={Wand2}
            title="AI does the work"
            desc="Our AI applies only your selected changes, preserving your brand identity."
          />
          <FeatureBox
            icon={ArrowRight}
            title="Download & launch"
            desc="Get the optimized image instantly, ready for your campaigns."
          />
        </motion.div>

        <p className="text-xs text-center text-slate-400 mt-8">
          Available on Growth plan and above. Predicted impact based on neuroscience benchmarks.
        </p>
      </div>
    </section>
  );
}

function KPI({ label, value, small }) {
  if (small) {
    return (
      <div className="text-center">
        <div className="text-base font-bold gradient-text" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{value}</div>
        <div className="text-[9px] text-slate-500 mt-0.5">{label}</div>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between">
      <span className="text-[11px] text-slate-500">{label}</span>
      <span className="text-sm font-bold gradient-text" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{value}</span>
    </div>
  );
}

function FeatureBox({ icon: Icon, title, desc }) {
  return (
    <div className="text-center">
      <div className="w-10 h-10 mx-auto rounded-xl bg-orange-50 flex items-center justify-center mb-3">
        <Icon size={18} className="text-orange-500" />
      </div>
      <h3 className="text-sm font-bold mb-1.5" style={{ fontFamily: 'Outfit, sans-serif', color: '#0F172A' }}>{title}</h3>
      <p className="text-xs leading-relaxed" style={{ color: '#64748B' }}>{desc}</p>
    </div>
  );
}
